import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const curriculum = [
  { title: 'What Are AI Agents?', duration: '30 min', lessons: 4 },
  { title: 'Agent Architectures & Design Patterns', duration: '42 min', lessons: 5 },
  { title: 'Tool Use & Function Calling', duration: '48 min', lessons: 6 },
  { title: 'Memory & State Management for Agents', duration: '38 min', lessons: 4 },
  { title: 'Planning & Reasoning in Agents', duration: '45 min', lessons: 5 },
  { title: 'Multi-Agent Systems & Collaboration', duration: '50 min', lessons: 5 },
  { title: 'Web Browsing & Data Retrieval Agents', duration: '40 min', lessons: 4 },
  { title: 'Code Generation & Execution Agents', duration: '42 min', lessons: 4 },
  { title: 'Deploying Agents to Production', duration: '55 min', lessons: 6 },
  { title: 'Capstone: Build a Full Autonomous Agent', duration: '60 min', lessons: 3 },
];

const skills = [
  'Agent Architecture', 'Tool Use & Function Calling', 'Memory Management',
  'Planning Algorithms', 'Multi-Agent Systems', 'LangChain', 'AutoGPT',
  'Web Browsing Agents', 'Production Deployment',
];

export default function AIAgentsPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 font-medium whitespace-nowrap bg-foreground-100 text-foreground-800 text-xs">
                  Advanced
                </span>
                <span className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 font-medium whitespace-nowrap bg-secondary-100 text-secondary-900 text-xs">
                  AI Agents
                </span>
              </div>

              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                Building AI Agents & Automation
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                Design and deploy autonomous AI agents that can browse the web, write code, and complete complex multi-step tasks. This advanced course covers agent architectures, tool use, memory, planning, and production deployment.
              </p>

              <div className="flex flex-wrap items-center gap-5 text-sm text-foreground-600 mb-6">
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center text-primary-500" />
                  10 hours
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-book-open-line w-4 h-4 flex items-center justify-center text-primary-500" />
                  55 lessons
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center text-primary-500" />
                  5,600 students
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill w-4 h-4 flex items-center justify-center text-primary-400" />
                  4.9 (620+ reviews)
                </span>
              </div>

              <div className="flex items-center gap-4">
                <span className="text-3xl font-heading font-extrabold text-foreground-950">$119</span>
                <Link
                  to="/checkout?course=ai-agents"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-primary-500 text-background-50 hover:bg-primary-600 h-11 px-6 text-sm"
                >
                  Enroll Now
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </Link>
              </div>
            </div>

            <div className="lg:w-80 w-full shrink-0">
              <div className="rounded-xl overflow-hidden border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Futuristic%20autonomous%20robot%20silhouette%20with%20interconnected%20circuit%20nodes%20on%20a%20clean%20minimal%20white%20background%2C%20abstract%20digital%20network%20visualization%2C%20soft%20ambient%20warm%20lighting%2C%20editorial%20tech%20photography%20style%2C%20sleek%20professional%20aesthetic%2C%20subtle%20gradient%20accents%2C%20no%20text%2C%20clean%20composition&width=640&height=400&seq=ai-agents-hero&orientation=landscape"
                  alt="Building AI Agents & Automation"
                  className="w-full h-48 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">Course Curriculum</h2>
              <div className="space-y-3">
                {curriculum.map((section, idx) => (
                  <div
                    key={section.title}
                    className="flex items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-full bg-background-100 text-foreground-500 text-xs font-semibold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">{section.title}</p>
                        <p className="text-xs text-foreground-500">{section.lessons} lessons</p>
                      </div>
                    </div>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">{section.duration}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading font-bold text-foreground-950 mb-3">Skills You Will Gain</h3>
                <div className="flex flex-wrap gap-1.5">
                  {skills.map((skill) => (
                    <span
                      key={skill}
                      className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 font-medium whitespace-nowrap bg-background-100 text-foreground-700 text-xs"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading font-bold text-foreground-950 mb-3">This Course Includes</h3>
                <ul className="space-y-2 text-sm text-foreground-600">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    10 hours on-demand video
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    55 downloadable resources
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    1 capstone project
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    Certificate of completion
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    Lifetime access
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    Community & Q&A access
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-14 bg-primary-500">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-background-50 mb-2">
            Build Your First AI Agent
          </h2>
          <p className="text-sm text-primary-100 mb-5 max-w-md mx-auto">
            Join 5,600+ students. One-time payment, lifetime access.
          </p>
          <Link
            to="/checkout?course=ai-agents"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-background-50 text-primary-600 hover:bg-background-100 h-10 px-5 text-sm"
          >
            Enroll for $119
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}